import type {
  DesignQualityEvidenceModel,
  DesignQualityIssue,
  ReferoStitchFactEvidence,
} from '@/features/validation/design-quality-panel-model'

interface DesignQualityEvidencePanelProps {
  model: DesignQualityEvidenceModel | null
}

const STATUS_CLASS: Record<DesignQualityEvidenceModel['status'], string> = {
  pass: 'border-success/25 bg-success/10 text-success dark:text-success',
  warn: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  fail: 'border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-300',
  'metric-only': 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-300',
}

const FACT_CLASS: Record<ReferoStitchFactEvidence['reviewStatus'], string> = {
  accepted: 'border-success/25 bg-success/10 text-success dark:text-success',
  'pending-review': 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  quarantine: 'border-red-500/25 bg-red-500/10 text-red-700 dark:text-red-300',
}

export function DesignQualityEvidencePanel({ model }: DesignQualityEvidencePanelProps) {
  if (!model) return null

  const issueGroups = [
    { label: 'Evidence source', items: model.issues.evidenceSource },
    { label: 'Style authority', items: model.issues.styleAuthority },
    { label: 'Raw style leak', items: model.issues.rawStyleLeaks },
    { label: 'Unsupported capability', items: model.issues.unsupportedCapabilities },
    { label: 'Visual diff', items: model.issues.visualDiffs },
    { label: 'Accessibility gap', items: model.issues.accessibilityGaps },
  ]

  return (
    <section
      data-testid="design-quality-evidence-panel"
      className="border-t border-border/60 bg-background/70"
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <div className="font-medium text-[11px] text-foreground">
          UI Design Quality Benchmark
        </div>
        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_CLASS[model.status]}`}>
          {model.status}
        </span>
        <div className="ml-auto flex flex-wrap justify-end gap-1 text-[10px]">
          <Metric label="score" value={String(model.summary.score)} />
          <Metric label="grade" value={model.summary.grade} />
          <Metric label="delta" value={formatDelta(model.summary.improvementDelta)} />
          <Metric label="hardening" value={model.summary.hardeningPassed ? 'passed' : 'needs review'} />
          <Metric label="source" value={model.evidence.sourceKind} />
          <Metric label="style objects" value={String(model.summary.authorStyleObjectCount)} />
        </div>
      </div>

      <div className="grid gap-2 px-3 pb-3 md:grid-cols-2">
        <div className="rounded border border-border/60 bg-background/80">
          <PanelTitle title="Design issues" detail="read-only evidence" />
          <div className="divide-y divide-border/50">
            {issueGroups.map((group) => (
              <IssueGroup key={group.label} label={group.label} items={group.items} />
            ))}
          </div>
        </div>

        <div className="rounded border border-border/60 bg-background/80">
          <PanelTitle
            title="Screenshot artifacts"
            detail={`${model.summary.artifactCount} artifact group(s) · ${model.evidence.sourceKind}`}
          />
          <div className="space-y-1.5 p-2">
            <div className="flex flex-wrap gap-1 text-[10px]">
              <Metric label="measured" value={model.evidence.measured ? 'yes' : 'no'} />
              <Metric label="screenshots" value={String(model.evidence.screenshotArtifactCount)} />
              <Metric label="traces" value={String(model.evidence.interactionTraceArtifactCount)} />
              {model.evidence.measuredBy ? <Metric label="by" value={model.evidence.measuredBy} /> : null}
            </div>
            {model.artifacts.map((artifact) => (
              <div
                key={`${artifact.viewport}:${artifact.currentPng ?? artifact.status}`}
                className="rounded border border-border/50 bg-muted/20 p-2 text-[10px]"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">{artifact.viewport}</span>
                  <span className="rounded border border-border/60 px-1 py-0.5 text-muted-foreground">
                    {artifact.status === 'available' ? 'artifact links' : 'contract metric only'}
                  </span>
                  {artifact.sourceId ? (
                    <span className="ml-auto truncate font-mono text-muted-foreground">{artifact.sourceId}</span>
                  ) : null}
                </div>
                {artifact.status === 'available' ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    <ArtifactLink label="current" href={artifact.currentPng} />
                    <ArtifactLink label="reference" href={artifact.referencePng} />
                    <ArtifactLink label="diff" href={artifact.diffPng} />
                  </div>
                ) : (
                  <div className="mt-1 text-muted-foreground">
                    No current/reference/diff PNG evidence attached for this viewport.
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded border border-border/60 bg-background/80">
          <PanelTitle title="Refero/Stitch facts" detail={`${model.referoStitchFacts.length} fact(s)`} />
          <div className="max-h-52 overflow-auto p-2">
            {model.referoStitchFacts.length === 0 ? (
              <div className="text-[10px] text-muted-foreground">No Refero/Stitch facts attached.</div>
            ) : (
              <ul className="space-y-1.5">
                {model.referoStitchFacts.map((fact) => (
                  <li
                    key={fact.id}
                    className="rounded border border-border/50 bg-muted/20 p-2 text-[10px]"
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`rounded border px-1 py-0.5 font-medium ${FACT_CLASS[fact.reviewStatus]}`}>
                        {fact.reviewStatus}
                      </span>
                      <span className="font-medium text-foreground">{fact.kind}</span>
                      <span className="font-mono text-muted-foreground">{fact.confidence.toFixed(2)}</span>
                    </div>
                    <div className="mt-1 grid gap-1 text-muted-foreground md:grid-cols-2">
                      <span className="truncate" title={`${fact.sourceSystem}:${fact.sourceId}`}>
                        source {fact.sourceSystem}:{fact.sourceId}
                      </span>
                      <span className="truncate" title={fact.normalizedTarget}>
                        target {fact.normalizedTarget}
                      </span>
                    </div>
                    {fact.reviewStatus === 'quarantine' ? (
                      <div className="mt-1 text-red-700 dark:text-red-300">
                        {fact.quarantineExcludedFromGenerator
                          ? 'Quarantined style excluded from generator context.'
                          : 'Quarantine leak risk: check generation context before use.'}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="rounded border border-border/60 bg-background/80">
          <PanelTitle title="Generator feedback" detail={model.generatorFeedback.shouldRetry ? 'retry guidance' : 'no retry'} />
          <div className="space-y-2 p-2 text-[10px]">
            {model.generatorFeedback.fallbackAllowedHardConstraintsPreserved ? (
              <div className="rounded border border-amber-500/25 bg-amber-500/10 px-2 py-1 font-medium text-amber-700 dark:text-amber-300">
                LLM fallback allowed but hard constraints preserved
              </div>
            ) : null}
            <FeedbackList label="Negative constraints" items={model.generatorFeedback.negativeConstraints} />
            <FeedbackList label="Fallback instructions" items={model.generatorFeedback.fallbackInstructions} />
            <FeedbackList label="Blocked reasons" items={model.generatorFeedback.blockedReasons} />
          </div>
        </div>
      </div>

      {model.profileSuite ? (
        <div className="px-3 pb-3">
          <div className="rounded border border-border/60 bg-background/80">
            <PanelTitle
              title="Profile suite"
              detail={`${model.profileSuite.summary.total} profile(s) · avg ${model.profileSuite.summary.averageScore}`}
            />
            <div className="flex flex-wrap gap-1.5 border-b border-border/50 px-2 py-2 text-[10px]">
              <Metric label="avg improvement" value={formatDelta(model.profileSuite.summary.averageImprovement)} />
              <Metric label="artifacts" value={String(model.profileSuite.summary.artifactCount)} />
              <Metric label="unsupported" value={String(model.profileSuite.summary.unsupportedCapabilityCount)} />
              <Metric label="hardening" value={model.profileSuite.summary.hardeningPassed ? 'passed' : 'needs review'} />
              {Object.entries(model.profileSuite.summary.gradeCounts).map(([grade, count]) => (
                <Metric key={grade} label={`grade ${grade}`} value={String(count)} />
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[10px]">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1 font-medium">Profile</th>
                    <th className="px-2 py-1 font-medium">Score</th>
                    <th className="px-2 py-1 font-medium">Grade</th>
                    <th className="px-2 py-1 font-medium">Raw leaks</th>
                    <th className="px-2 py-1 font-medium">Style objects</th>
                    <th className="px-2 py-1 font-medium">Visual fidelity</th>
                    <th className="px-2 py-1 font-medium">Capability evidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {model.profileSuite.rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-2 py-1 font-medium text-foreground">{row.label}</td>
                      <td className="px-2 py-1">{row.score}</td>
                      <td className="px-2 py-1">{row.grade}</td>
                      <td className="px-2 py-1">{row.rawLeaks}</td>
                      <td className="px-2 py-1">{row.styleObjects}</td>
                      <td className="px-2 py-1">{row.visualFidelity}</td>
                      <td className="px-2 py-1">{row.capabilityEvidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function PanelTitle({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border/50 px-2 py-1.5 text-[10px]">
      <span className="font-medium text-foreground">{title}</span>
      <span className="text-muted-foreground">{detail}</span>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-border/60 bg-background/80 px-1.5 py-0.5">
      <span className="font-semibold uppercase text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </span>
  )
}

function IssueGroup({ label, items }: { label: string; items: DesignQualityIssue[] }) {
  return (
    <div className="p-2 text-[10px]">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-muted-foreground">No issue evidence.</div>
      ) : (
        <ul className="space-y-1">
          {items.map((item) => (
            <li key={item.id} className="rounded border border-border/50 bg-muted/20 px-2 py-1">
              <div className="font-medium text-foreground">{item.message}</div>
              {item.detail ? <div className="mt-0.5 text-muted-foreground">{item.detail}</div> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ArtifactLink({ label, href }: { label: string; href?: string }) {
  if (!href) return null
  return (
    <a
      href={href}
      className="rounded border border-border/60 bg-background px-1.5 py-0.5 font-medium text-primary hover:underline"
    >
      {label}
    </a>
  )
}

function FeedbackList({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <div className="mb-1 font-medium text-foreground">{label}</div>
      {items.length === 0 ? (
        <div className="text-muted-foreground">None.</div>
      ) : (
        <ul className="space-y-1">
          {items.map((item) => (
            <li key={item} className="rounded border border-border/50 bg-muted/20 px-2 py-1 text-muted-foreground">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function formatDelta(value: number): string {
  if (value === 0) return '0'
  return value > 0 ? `+${value}` : String(value)
}
